/**
 * API Client utility for making API calls
 * Provides a consistent interface for all API requests
 */

/**
 * Makes an API request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {Object} options.body - Request body (will be JSON stringified)
 * @param {Object} options.headers - Additional headers
 * @param {boolean} options.credentials - Include credentials (default: true)
 * @param {string} options.apiLink - Base API URL (from context)
 * @returns {Promise<Response>} Fetch response
 */
export const apiRequest = async (endpoint, options = {}) => {
    const {
        method = 'GET',
        body = null,
        headers = {},
        credentials = 'include',
        apiLink
    } = options;

    const config = {
        method,
        credentials,
        headers: {
            'Content-Type': 'application/json',
            ...headers
        }
    };

    if (body && method !== 'GET') {
        config.body = JSON.stringify(body);
    }

    const url = apiLink ? `${apiLink}${endpoint}` : endpoint;
    return fetch(url, config);
};

/**
 * Makes an API request and returns parsed JSON data
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Request options (same as apiRequest)
 * @param {Function} options.onSuccess - Callback function called on success
 * @param {Function} options.onError - Callback function called on error
 * @returns {Promise<Object>} Parsed JSON response data
 */
export const apiCall = async (endpoint, options = {}) => {
    const { onSuccess, onError, ...requestOptions } = options;

    try {
        const response = await apiRequest(endpoint, requestOptions);

        if (response.ok) {
            const data = await response.json();
            if (onSuccess) {
                onSuccess(data, response);
            }
            return { success: true, data, response };
        } else {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            if (onError) {
                onError(errorData, response);
            }
            return { success: false, error: errorData, response };
        }
    } catch (error) {
        console.error('API call failed:', error);
        if (onError) {
            onError({ error: error.message }, null);
        }
        return { success: false, error: { error: error.message } };
    }
};

/**
 * Hook-like function for API calls (can be used in components)
 * @param {string} apiLink - Base API URL from context
 * @returns {Object} Object with apiCall method bound to the API link
 */
export const useApiClient = (apiLink) => {
    const call = (endpoint, options = {}) => {
        return apiCall(endpoint, { ...options, apiLink });
    };

    return { call, apiLink };
};

